import React, { useEffect, useState, useRef, useCallback } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from "moment";
import * as XLSX from "xlsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  reportingSkills,
  reportingNonTechSkills,
  reportingLocations,
  reportingExperiences,
  reportingIdentify,
} from "../../api/skillm";

const Reporting = () => {
  const [talentData, setTalentData] = useState([]);
  const [cityData, setCityData] = useState([]);
  const [experienceData, setExperienceData] = useState([]);
  const [itSkillsData, setItSkillsData] = useState([]);
  const [nonItSkillsData, setNonItSkillsData] = useState([]);

  const [formInput, setFormInput] = useState({
    from_date: new Date(),
    to_date: new Date(),
    limit: "1",
  });

  const reportRef = useRef();

  useEffect(() => {
    setAllData();
  }, []);

  const setAllData = useCallback(
    async (filter = false) => {
      let query = "";
      if (filter) {
        let params = {
          ...formInput,
          from_date: moment(formInput.from_date).format("yyyy-MM-DD"),
          to_date: moment(formInput.to_date).format("yyyy-MM-DD"),
        };
        query = new URLSearchParams(params).toString();
      }
      let skill = await reportingSkills(query);
      setItSkillsData(await filterData(skill));
      let identify = await reportingIdentify(query);
      setTalentData(await filterData(identify));
      let expr = await reportingExperiences(query);
      setExperienceData(await filterData(expr));
      let city = await reportingLocations(query);
      setCityData(await filterData(city));
      let notech = await reportingNonTechSkills(query);
      setNonItSkillsData(await filterData(notech));
    },
    [formInput]
  );

  const filterData = async (skill) => {
    return skill.map((item) => {
      let [label, percentage] = Object.entries(item)[0];
      percentage = parseInt(percentage);
      return { label, percentage: isNaN(percentage) ? 0 : percentage };
    });
  };

  const renderProgressBars = (data, colorClass) =>
    data.map((item, index) => (
      <div key={index} className="row mb-2">
        <div className="col-4">
          <p className="mb-0">{item.label}</p>
        </div>
        <div className="col-8">
          <div className="progress_new">
            <div
              className={`progress-bar ${colorClass}`}
              role="progressbar"
              aria-valuenow={item.percentage}
              aria-valuemin="0"
              aria-valuemax="100"
              style={{ width: `${item.percentage}%` }}
            >
              {item.percentage}%
            </div>
          </div>
        </div>
      </div>
    ));

  const downloadAsImage = async () => {
    // Ensure the background is set to white
    reportRef.current.style.backgroundColor = "white";

    const canvas = await html2canvas(reportRef.current, {
      backgroundColor: "white", // Ensures transparent areas are white
    });

    const imgData = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = imgData;
    link.download = "report.png";
    link.click();

    // Restore the original background
    reportRef.current.style.backgroundColor = "";
  };

  const downloadAsPDF = async () => {
    // Ensure the background is set to white
    reportRef.current.style.backgroundColor = "white";

    const canvas = await html2canvas(reportRef.current, {
      backgroundColor: "white", // Ensures transparent areas are white
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("portrait", "mm", "a4");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save("report.pdf");

    // Restore the original background
    reportRef.current.style.backgroundColor = "";
  };

  const limitNumber = useCallback(() => {
    let options = [];
    for (let index = 1; index <= 20; index++) {
      options.push(
        <option value={index} key={index}>
          {index}
        </option>
      );
    }
    return options;
  }, []);

  const setFormValue = useCallback(
    (name, value) => {
      setFormInput({ ...formInput, [name]: value });
    },
    [formInput, setFormInput]
  );

  const clearFilter = useCallback(() => {
    setFormInput({
      from_date: new Date(),
      to_date: new Date(),
      limit: "1",
    });
    setAllData();
  }, [setAllData, setFormInput]);

  const downloadExcel = useCallback(() => {
    // Prepare the data for Excel export
    //  const worksheetData = data.map((d) => {
    //    const row = { Skill: d.skill_title };
    //    identity.forEach((identityItem, index) => {
    //      row[identityItem.identify_name] = d[identityItem.identify_name]
    //        ? d[identityItem.identify_name]
    //        : 0;
    //    });
    //    return row;
    //  });

    let worksheetData = [{ label: "Telent Date", percentage: "" }];

    talentData.forEach((item, index) => {
      worksheetData.push(item);
    });
    worksheetData.push({ label: "", percentage: "" });
    worksheetData.push({ label: "CITY", percentage: "" });
    cityData.forEach((item, index) => {
      worksheetData.push(item);
    });

    worksheetData.push({ label: "", percentage: "" });
    worksheetData.push({ label: "WORK EXPERIENCE", percentage: "" });
    experienceData.forEach((item, index) => {
      worksheetData.push(item);
    });

    worksheetData.push({ label: "", percentage: "" });
    worksheetData.push({ label: "IT SKILLS", percentage: "" });
    itSkillsData.forEach((item, index) => {
      worksheetData.push(item);
    });

    worksheetData.push({ label: "", percentage: "" });
    worksheetData.push({ label: "NON-IT SKILLS", percentage: "" });
    nonItSkillsData.forEach((item, index) => {
      worksheetData.push(item);
    });

    // Convert to worksheet
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);

    // Create a new workbook and add the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Identity Matrix");

    // Save the file
    XLSX.writeFile(workbook, "reportMetrix.xlsx");
  }, [talentData, cityData, experienceData, itSkillsData, nonItSkillsData]);

  return (
    <div>
      <div className="row mb-3">
        <div className="col-md-7">
          <div className="row">
            <div className="col-md-3">
              <label htmlFor="inputEmail4" className="form-label">
                From Date
              </label>
              <DatePicker
                className={`form-control`}
                dateFormat="Y-MM-dd"
                maxDate={new Date()}
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                placeholderText="From Date"
                onFocus={(e) => e.target.blur()}
                selected={formInput?.from_date}
                onChange={(date) => setFormValue("from_date", date)}
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="inputPassword4" className="form-label">
                To Date
              </label>
              <div>
                <DatePicker
                  className={`form-control`}
                  dateFormat="Y-MM-dd"
                  maxDate={new Date()}
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  placeholderText="To Date"
                  onFocus={(e) => e.target.blur()}
                  selected={formInput?.to_date}
                  onChange={(date) => setFormValue("to_date", date)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <label htmlFor="inputPassword4" className="form-label">
                Select Limit
              </label>
              <select
                value={formInput?.limit}
                className="form-select"
                onChange={(e) => setFormValue("limit", e.target.value)}
              >
                {limitNumber()}
              </select>
            </div>
            <div className="col-md-3 mt-2">
              <br />
              <button
                onClick={() => setAllData(true)}
                className="btn btn-sm btn-success me-2"
              >
                <i className="fa fa-search"></i>
              </button>
              <button
                onClick={clearFilter}
                className="btn btn-sm btn-primary me-2"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="col-md-5 d-flex align-items-end justify-content-end">
          <button
            className="btn btn-sm btn-warning me-2"
            onClick={downloadExcel}
          >
            Download Xl
          </button>
          <button
            className="btn btn-sm btn-primary me-2"
            onClick={downloadAsImage}
          >
            Download as Image
          </button>
          <button className="btn btn-sm btn-primary" onClick={downloadAsPDF}>
            Download as PDF
          </button>
        </div>
      </div>

      <div className="card" ref={reportRef}>
        <div className="card-body">
          <div className="row">
            <div className="col-md-4">
              <h4>TALENT</h4>
              <br />
              {renderProgressBars(talentData, "bg-danger")}
            </div>
            <div className="col-md-4">
              <h4>CITY</h4>
              <br />
              {renderProgressBars(cityData, "bg-warning")}
            </div>
            <div className="col-md-4">
              <h4>WORK EXPERIENCE</h4>
              <br />
              {renderProgressBars(experienceData, "bg-success")}
            </div>
          </div>

          <div className="row">
            <div className="col-md-4">
              <h4>IT SKILLS</h4>
              <br />
              {renderProgressBars(itSkillsData, "bg-info")}
            </div>
            <div className="col-md-4">
              <h4>NON-IT SKILLS</h4>
              <br />
              {renderProgressBars(
                nonItSkillsData,
                "progress-bar-striped bg-secondary"
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reporting;
